import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ArchivePopup from '../../../components/Popup/ArchivePopup/ArchivePopup';
import DeletePopup from '../../../components/Popup/DeletePopup/DeletePopup';
import EditIcon from '../../../components/Table/EditIcon/EditIcon';
import DetailIcon from '../../../components/Table/DetailIcon/DetailIcon';
import ArchiveIcon from '../../../components/Table/ArchiveIcon/ArchiveIcon';
import DeleteIcon from '../../../components/Table/DeleteIcon/DeleteIcon';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import AddButton from '../../../components/common/AddButton/AddButton';
import SearchBox from '../../../components/common/Search/SearchBox';
import Filter from '../../../components/common/Filter/Filter';
import Sort from '../../../components/common/Sort/Sort';
import GroupBy from '../../../components/common/GroupBy/GroupBy';
import ArchiveSelectedItem from '../../../components/common/ArchiveSelectedItemLabel/ArchiveSelectedItems';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';
import { ArchiveAlert } from '../../../components/common/AlertBox/ArchiveAlert';
import '../../../components/common/SubNavbar.css';
import '../../../components/common/CommonText.css';
import '../../../components/common/Table.css';
import './States.css';
import { baseurl } from '../../../api';
import { useCheckboxClick } from '../../../checkboxclick';
import authenticatedAxios from '../../../authenticatedAxios';
import useFilterStore from '../../../store/useFilterStore';
import useRefreshStore from '../../../store/useRefreshStore';

const pageKey = 'states';

const States = () => {
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
  const [items, setItems] = useState([]);

  const sortOptions = ['A-Z', 'Z-A', 'Newest', 'Oldest'];
  const filterOptions = ['Default', 'Archived', 'Unarchived'];
  const groupbyOptions = ['Default', 'Country'];
  const searchCategories = ['State', 'Country'];

  const location = useLocation();
  const { countryId } = location.state || {};
  const navigate = useNavigate();

  const [groupedItems, setGroupedItems] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedGroupByOption, setSelectedGroupByOption] = useState('Default');
  const [searchParams, setSearchParams] = useState(savedFilters?.searchParams || { term: '' });
  const [selectedFilterOption, setSelectedFilterOption] = useState(savedFilters?.selectedFilterOption || 'Unarchived');
  const [selectedSortOption, setSelectedSortOption] = useState(savedFilters?.selectedSortOption || 'Newest');

  const {
    isParentSelected,
    selectedItems,
    handleParentCheckboxChange,
    handleChildCheckboxChange,
    resetCheckboxState,
  } = useCheckboxClick(items);

  const { refreshKeys, triggerRefresh } = useRefreshStore();
  const refreshKey = refreshKeys['States'] || 0;

  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case 'A-Z':
        return 'a_to_z';
      case 'Z-A':
        return 'z_to_a';
      case 'Newest':
        return 'newest';
      case 'Oldest':
        return 'oldest';
      default:
        return 'newest';
    }
  };

  const fetchStates = async () => {
    try {
      const params = {
        search: searchParams.term,
        search_argument: searchParams.argument,
        filter: selectedFilterOption === 'Default' ? undefined : selectedFilterOption.toLowerCase(),
        sort: mapSortOptionToQuery(selectedSortOption),
        group_by: selectedGroupByOption.toLowerCase(),
      };

      if (countryId) {
        params.country = countryId;
      }

      const response = await authenticatedAxios.get(`${baseurl}localization/state/`, { params });
      const result = response.data;

      if (result.response_code === 1) {
        setItems(result.data);
        if (selectedGroupByOption !== 'Default') {
          groupItems(result.data, selectedGroupByOption);
        }
      }
    } catch (error) {
      if (error?.response?.data?.error_code === 1001) {
        navigate('/permission-denied');
      } else {
        console.error('Error fetching states:', error);
      }
      setItems([]);
    }
  };

  const groupItems = (data, groupBy) => {
    const grouped = data.reduce((acc, item) => {
      let key;
      switch (groupBy.toLowerCase()) {
        case 'country':
          key = item.country?.name || 'Unknown Country';
          break;
        default:
          key = 'default';
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    setGroupedItems(grouped);
  };

  useEffect(() => {
    fetchStates();
    setFilters(pageKey, {
      searchParams,
      selectedFilterOption,
      selectedSortOption,
    });
  }, [searchParams, selectedFilterOption, selectedSortOption, selectedGroupByOption]);

  useEffect(() => {
    fetchStates();
  }, [refreshKey]);

  const handleGroupBySelect = (option) => {
    setSelectedGroupByOption(option);
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const handleDetailClick = (state) => {
    navigate(`/states/detail/${state.id}`);
  };

  const handleEditClick = (state) => {
    navigate(`/states/edit/${state.id}`);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setIsDeletePopupOpen(true);
  };

  const handleArchiveClick = (item) => {
    setSelectedItem(item);
    setIsArchivePopupOpen(true);
  };

  const handleDelete = async () => {
    try {
      const response = await authenticatedAxios.delete(
        `${baseurl}localization/state/?id=${selectedItem.id}`
      );

      if (response.status >= 200 && response.status < 300) {
        setIsDeletePopupOpen(false);
        setSelectedItem(null);
        SuccessAlert('State deleted successfully');
        fetchStates();
        triggerRefresh('States');
        return;
      }
      throw new Error(`Unexpected status code: ${response.status}`);
    } catch (error) {
      console.error('Error deleting state:', error);
    }
  };

  const handleArchive = async () => {
    try {
      const response = await authenticatedAxios.get(
        `${baseurl}localization/state/archive/?id=${selectedItem.id}`
      );

      if (response.status >= 200 && response.status < 300) {
        await fetchStates();
        setIsArchivePopupOpen(false);
        ArchiveAlert(
          `State ${selectedItem.is_archived ? 'unarchived' : 'archived'} successfully`,
          'success'
        );
        setSelectedItem(null);
      }
    } catch (error) {
      console.error('Error archiving state:', error);
    }
  };

  const handleArchiveSelectedItems = async () => {
    try {
      const response = await authenticatedAxios.post(`${baseurl}localization/state/archive/`, {
        id_list: selectedItems,
      });

      if (response.status >= 200 && response.status < 300) {
        fetchStates();
        resetCheckboxState();
        setIsArchivePopupOpen(false);
        ArchiveAlert('Selected states archive status updated successfully', 'success');
      } else {
        console.error('Failed to archive/unarchive selected items.');
      }
    } catch (error) {
      console.error('Error archiving/unarchiving selected items:', error);
    }
  };

  const handleSearchSelect = (searchCategory, searchTerm) => {
    setSearchParams({
      term: searchTerm,
      argument: searchCategory.toLowerCase(),
    });
  };

  const handleSortOptionSelect = (option) => {
    setSelectedSortOption(option);
  };

  const handleFilterSelect = (option) => {
    setSelectedFilterOption(option);
  };

  const closePopups = () => {
    setIsDeletePopupOpen(false);
    setIsArchivePopupOpen(false);
    setSelectedItem(null);
  };

  const handleExportComplete = () => {
    resetCheckboxState();
  };

  const deletePopupFields = [
    {
      label: 'Are you sure you want to delete this record? Once deleted, it cannot be recovered.',
      type: 'text',
    },
  ];

  const renderTableRows = (states) => {
    return states.length > 0 ? (
      states.map((state) => (
        <tr key={state.id} className={state.is_archived ? 'archived-item' : ''}>
          <td className="state-label">
            <input
              className="table-selectbox table-child-selectbox"
              type="checkbox"
              checked={selectedItems.includes(state.id)}
              onChange={() => handleChildCheckboxChange(state.id)}
            />
            <span
              onClick={() => handleDetailClick(state)}
              className="blue cr-pointer"
              title="Click to view details"
            >
              {state.name}
            </span>
          </td>
          <td>{state.state_code || 'N/A'}</td>
          <td>
            <span
              onClick={() => state.country?.id && navigate(`/countries/detail/${state.country.id}`)}
              className="cr-pointer blue"
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              {state.country?.country_flag && typeof state.country.country_flag === 'string' && (
                <img
                  src={`${baseurl.replace(/\/$/, '')}${state.country.country_flag.startsWith('/') ? '' : '/'}${state.country.country_flag}`}
                  alt={state.country.name}
                  className="country-icon"
                />
              )}
              {state.country?.name || 'Unknown Country'}
            </span>
          </td>
          <td className="actions common-action">
            <DetailIcon onClick={() => handleDetailClick(state)} />
            <EditIcon onClick={() => handleEditClick(state)} />
            <ArchiveIcon
              onClick={() => handleArchiveClick(state)}
              isArchived={state.is_archived}
            />
            <DeleteIcon onClick={() => handleDeleteClick(state)} />
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
          No states found.
        </td>
      </tr>
    );
  };

  const renderGroupedTable = () => {
    if (selectedGroupByOption === 'Default') {
      return (
        <tbody className="common-table-tbody">
          {renderTableRows(items)}
        </tbody>
      );
    }
    return (
      <tbody className="common-table-tbody">
        {Object.entries(groupedItems).map(([groupName, groupStates]) => (
          <React.Fragment key={groupName}>
            <tr className="group-header-row">
              <td colSpan="4" className="group-header">
                <div
                  className="group-header-content"
                  onClick={() => toggleGroup(groupName)}
                >
                  <svg
                    className={`expand-item-icon ${expandedGroups.has(groupName) ? 'rotated' : ''}`}
                    width="7"
                    height="13"
                    viewBox="0 0 7 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M7 6.5L0.25 12.9952L0.250001 0.00480908L7 6.5Z" fill="#424242" />
                  </svg>
                  <span className="group-name common-text fts-15 blue">{groupName}</span>
                  <span className="group-count common-text fts-15 blue">({groupStates.length})</span>
                </div>
              </td>
            </tr>
            {expandedGroups.has(groupName) && renderTableRows(groupStates)}
          </React.Fragment>
        ))}
      </tbody>
    );
  };

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader pageName=" Localization /" nestedpageName="States" linkTo="" />
        <div className="export-container">
          <ArchiveSelectedItem
            selectedItems={selectedItems}
            items={items}
            onArchiveSelected={handleArchiveSelectedItems}
          />
          <ExportLabel
            exportUrl={`${baseurl}localization/state/export/`}
            selectedIds={selectedItems}
            onExportComplete={handleExportComplete}
          />
        </div>
      </div>

      <div className="pagename-container">
        <div className="page-name">
          <h1 className="common-text fts-22 ftw-6 blue">States</h1>
        </div>
        <div className="pagename-right-container">
          <AddButton
            onClick={() =>
              navigate('/states/create', { state: countryId ? { countryId } : {} })
            }
          />
          <SearchBox
            categories={searchCategories}
            onSearchSelect={handleSearchSelect}
            defaultValue={searchParams?.term}
          />
          <GroupBy
            options={groupbyOptions}
            selectedOption={selectedGroupByOption}
            onSelect={handleGroupBySelect}
          />
          <Filter
            options={filterOptions}
            onFilterSelect={handleFilterSelect}
            defaultValue={selectedFilterOption}
          />
          <Sort
            options={sortOptions}
            onOptionSelect={handleSortOptionSelect}
            defaultValue={selectedSortOption}
          />
        </div>
      </div>

      <div className="table-container">
        <table className="common-table">
          <thead className="common-table-head">
            <tr className="common-table-thead-tr">
              <th>
                <input
                  className="table-selectbox table-parent-selectbox"
                  type="checkbox"
                  checked={isParentSelected}
                  onChange={handleParentCheckboxChange}
                />
                State Name
              </th>
              <th>State Code</th>
              <th>Country</th>
              <th className="for-hide">Actions</th>
            </tr>
          </thead>
          {renderGroupedTable()}
        </table>
      </div>

      <ArchivePopup
        isOpen={isArchivePopupOpen}
        isArchived={selectedItem?.is_archived}
        archivename={selectedItem?.name}
        onSave={handleArchive}
        onClose={() => setIsArchivePopupOpen(false)}
      />

      <DeletePopup
        isOpen={isDeletePopupOpen}
        title="Delete State"
        fields={deletePopupFields}
        onSave={handleDelete}
        onClose={closePopups}
      />
    </div>
  );
};

export default States;
